import { useEffect, useState } from "react";
import api, { apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useBranch } from "@/context/BranchContext";
import { PageHeader } from "@/components/PageHeader";
import { StatusBadge } from "@/components/StatusBadge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { fmtDateTime, fmtGHS, pName } from "@/lib/format";
import { printDoc, esc } from "@/lib/print";
import { Plus, Trash2, ArrowLeftRight, Truck, PackageCheck, Send, Printer, AlertTriangle, Wrench, Pencil, Sparkles } from "lucide-react";
import { toast } from "sonner";
import { ProductCombobox } from "@/components/ProductCombobox";

const lineDispatched = (l) => (l.allocations || []).reduce((s, a) => s + a.quantity, 0);
const ageLabel = (iso) => {
  if (!iso) return "";
  const mins = Math.max(0, Math.round((Date.now() - new Date(iso).getTime()) / 60000));
  if (mins < 60) return `${mins}m`;
  const h = Math.floor(mins / 60);
  if (h < 24) return `${h}h ${mins % 60}m`;
  return `${Math.floor(h / 24)}d ${h % 24}h`;
};
const lineCostTotal = (l) => {
  const allocs = l.allocations || [];
  if (allocs.length) return allocs.reduce((s, a) => s + (a.cost_price || 0) * a.quantity, 0);
  return (l.quantity || 0) * (l.cost_price || 0);
};

export default function Transfers() {
  const { can, user } = useAuth();
  const { branches, branchParam, allActive, warehouses } = useBranch();
  const [transfers, setTransfers] = useState([]);
  const [products, setProducts] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ type: "transfer", from_branch_id: "", to_branch_id: "", note: "", lines: [] });
  const [srcStock, setSrcStock] = useState({});
  const [sel, setSel] = useState(null);
  const [recvOpen, setRecvOpen] = useState(false);
  const [recvT, setRecvT] = useState(null);
  const [recvQty, setRecvQty] = useState({});
  const [resOpen, setResOpen] = useState(false);
  const [resT, setResT] = useState(null);
  const [sfRes, setSfRes] = useState("return_to_source");
  const [ovRes, setOvRes] = useState("return_to_source");
  const [resNote, setResNote] = useState("");
  const [rejOpen, setRejOpen] = useState(false);
  const [rejT, setRejT] = useState(null);
  const [rejNote, setRejNote] = useState("");
  const [editOpen, setEditOpen] = useState(false);
  const [editT, setEditT] = useState(null);
  const [editLines, setEditLines] = useState([]);
  const [editStock, setEditStock] = useState({});

  const load = () => api.get("/transfers", { params: { branch_id: branchParam } }).then((r) => setTransfers(r.data));
  useEffect(() => { load(); }, [branchParam]); // eslint-disable-line
  useEffect(() => { api.get("/products").then((r) => setProducts(r.data)); }, []);

  // load source-branch stock as an availability hint — ONLY for direct transfers
  // (a requesting branch must not see the warehouse's stock levels)
  useEffect(() => {
    if (form.type !== "transfer" || !form.from_branch_id) { setSrcStock({}); return; }
    api.get("/stock", { params: { branch_id: form.from_branch_id } })
      .then((r) => setSrcStock(Object.fromEntries(r.data.map((s) => [s.product_id, s.total_qty]))))
      .catch(() => setSrcStock({}));
  }, [form.type, form.from_branch_id]);

  const addLine = () => setForm((f) => ({ ...f, lines: [...f.lines, { product_id: "", quantity: 1 }] }));
  const setLine = (i, k, v) => setForm((f) => ({ ...f, lines: f.lines.map((l, idx) => idx === i ? { ...l, [k]: v } : l) }));
  const rmLine = (i) => setForm((f) => ({ ...f, lines: f.lines.filter((_, idx) => idx !== i) }));
  const [suggesting, setSuggesting] = useState(false);
  const suggest = async () => {
    if (!form.from_branch_id || !form.to_branch_id) return toast.error("Select the warehouse and the destination branch first");
    setSuggesting(true);
    try {
      const { data } = await api.get("/transfer-request-suggestions", { params: { from_branch_id: form.from_branch_id, to_branch_id: form.to_branch_id } });
      if (!data.lines.length) toast.info(data.unavailable ? `${data.unavailable} low-stock item(s) but the warehouse has none in stock` : "No low-stock items at this branch");
      else {
        setForm((f) => ({ ...f, lines: data.lines.map((l) => ({ product_id: l.product_id, quantity: l.suggested_qty })) }));
        toast.success(`${data.lines.length} low-stock item(s) added` + (data.unavailable ? ` · ${data.unavailable} not available at the warehouse` : ""));
      }
    } catch (e) { toast.error(apiError(e)); } finally { setSuggesting(false); }
  };

  const openNew = (type) => {
    setForm({ type, from_branch_id: type === "request" ? (warehouses[0]?.id || "") : "", to_branch_id: "", note: "", lines: [{ product_id: "", quantity: 1 }] });
    setOpen(true);
  };

  const create = async () => {
    const lines = form.lines.filter((l) => l.product_id).map((l) => ({ product_id: l.product_id, quantity: Number(l.quantity) || 0 })).filter((l) => l.quantity > 0);
    if (!form.from_branch_id || !form.to_branch_id) return toast.error("Select both source and destination");
    if (!lines.length) return toast.error("Add at least one item");
    try {
      await api.post("/transfers", { type: form.type, from_branch_id: form.from_branch_id, to_branch_id: form.to_branch_id, note: form.note, lines });
      toast.success(form.type === "request" ? "Stock request drafted" : "Transfer drafted"); setOpen(false); load();
    } catch (e) { toast.error(apiError(e)); }
  };

  const act = async (id, action) => {
    try { await api.post(`/transfers/${id}/${action}`); toast.success(`Transfer ${action}ed`); load(); }
    catch (e) { toast.error(apiError(e)); }
  };

  const openReceive = (t) => {
    setRecvT(t);
    setRecvQty(Object.fromEntries(t.lines.map((l) => [l.product_id, lineDispatched(l)])));
    setRecvOpen(true);
  };
  const submitReceive = async () => {
    try {
      const lines = recvT.lines.map((l) => ({ product_id: l.product_id, received_qty: Number(recvQty[l.product_id] ?? lineDispatched(l)) }));
      const { data } = await api.post(`/transfers/${recvT.id}/receive`, { lines });
      toast[data.status === "discrepancy" ? "warning" : "success"](data.status === "discrepancy" ? "Received with a discrepancy — flagged for the sending branch" : "Transfer received in full");
      setRecvOpen(false); setRecvT(null); load();
    } catch (e) { toast.error(apiError(e)); }
  };

  const openResolve = (t) => { setResT(t); setSfRes("return_to_source"); setOvRes("return_to_source"); setResNote(""); setResOpen(true); };
  const submitResolve = async () => {
    if (!resNote.trim()) return toast.error("Add an investigation note");
    try {
      await api.post(`/transfers/${resT.id}/resolve`, { shortfall_resolution: sfRes, overage_resolution: ovRes, note: resNote });
      toast.success(`Resolution proposed — sent to ${resT.from_branch_name} for approval`); setResOpen(false); setResT(null); load();
    } catch (e) { toast.error(apiError(e)); }
  };

  const approveRes = async (t) => {
    try { await api.post(`/transfers/${t.id}/approve-resolution`); toast.success("Resolution approved — stock updated"); load(); }
    catch (e) { toast.error(apiError(e)); }
  };
  const openReject = (t) => { setRejT(t); setRejNote(""); setRejOpen(true); };
  const submitReject = async () => {
    if (!rejNote.trim()) return toast.error("Add a reason for rejecting");
    try { await api.post(`/transfers/${rejT.id}/reject-resolution`, { note: rejNote }); toast.success("Proposal rejected — sent back to the receiver"); setRejOpen(false); setRejT(null); load(); }
    catch (e) { toast.error(apiError(e)); }
  };

  // Warehouse edits a draft request to reflect what it can actually supply
  const openEdit = (t) => {
    setEditT(t);
    setEditLines(t.lines.map((l) => ({ product_id: l.product_id, generic_name: l.generic_name, sku: l.sku, quantity: lineDispatched(l) || l.quantity })));
    api.get("/stock", { params: { branch_id: t.from_branch_id } })
      .then((r) => setEditStock(Object.fromEntries(r.data.map((s) => [s.product_id, s.total_qty]))))
      .catch(() => setEditStock({}));
    setEditOpen(true);
  };
  const setEditQty = (pid, v) => setEditLines((ls) => ls.map((l) => l.product_id === pid ? { ...l, quantity: v } : l));
  const rmEditLine = (pid) => setEditLines((ls) => ls.filter((l) => l.product_id !== pid));
  const submitEdit = async () => {
    const lines = editLines.map((l) => ({ product_id: l.product_id, quantity: Number(l.quantity) || 0 })).filter((l) => l.quantity > 0);
    if (!lines.length) return toast.error("Keep at least one item with a quantity");
    try {
      await api.post(`/transfers/${editT.id}/update`, { lines });
      toast.success("Draft updated"); setEditOpen(false); setEditT(null); load();
    } catch (e) { toast.error(apiError(e)); }
  };

  const printTransfer = (t) => {
    const resolved = t.status === "resolved";
    const received = t.received_at != null || t.lines.some((l) => l.received_qty != null);
    const unitCost = (l) => { const disp = lineDispatched(l) || l.quantity || 0; const tot = lineCostTotal(l); return disp ? tot / disp : (l.cost_price || 0); };
    // Final units accounted at the destination after any agreed resolution
    const settledQty = (l) => {
      const disp = lineDispatched(l) || l.quantity || 0;
      const rec = l.received_qty != null ? l.received_qty : disp;
      if (resolved && (l.overage || 0) > 0 && t.overage_resolution === "return_to_source") return rec - (l.overage || 0);
      return rec;
    };
    const rows = t.lines.map((l) => {
      const disp = lineDispatched(l) || l.quantity;
      const rec = l.received_qty != null ? l.received_qty : null;
      const settled = settledQty(l);
      const unit = unitCost(l);
      const varParts = [];
      if ((l.shortfall || 0) > 0) varParts.push(`short ${l.shortfall}`);
      if ((l.overage || 0) > 0) varParts.push(`over ${l.overage}`);
      const vcell = varParts.length ? `<span style="color:#b45309">${varParts.join(", ")}</span>` : "—";
      return `<tr><td>${esc(l.generic_name)} <span style="color:#666">${esc(l.sku)}</span></td><td class="r">${disp}</td><td class="r">${rec != null ? rec : "—"}</td><td class="r">${vcell}</td><td class="r"><strong>${settled}</strong></td><td class="r">${fmtGHS(unit)}</td><td class="r">${fmtGHS(unit * settled)}</td></tr>`;
    }).join("");
    const grand = t.lines.reduce((s, l) => s + unitCost(l) * settledQty(l), 0);
    const anyShort = t.lines.some((l) => (l.shortfall || 0) > 0);
    const anyOver = t.lines.some((l) => (l.overage || 0) > 0);
    let resolutionLine = "";
    if (resolved && (anyShort || anyOver || t.resolution)) {
      const parts = [];
      if (t.shortfall_resolution || anyShort) parts.push(`Shortfall — ${(t.shortfall_resolution || "") === "writeoff" ? "written off (lost in transit)" : "returned to source"}`);
      if (t.overage_resolution || anyOver) parts.push(`Over-receipt — ${(t.overage_resolution || "") === "keep" ? "kept at receiving branch" : "returned to source"}`);
      const label = parts.length ? parts.join("; ") : (t.resolution === "writeoff" ? "Write-off (lost in transit)" : "Returned to source");
      const who = t.approved_by || t.resolved_by;
      resolutionLine = `<div class="muted" style="margin-top:6px">Discrepancy resolution: ${label}${t.resolution_note ? ` — ${esc(t.resolution_note)}` : ""}${who ? ` (approved by ${esc(who)})` : ""}</div>`;
    } else if (t.status === "pending_approval") {
      const parts = [];
      if (t.pending_shortfall_resolution) parts.push(`Shortfall — ${t.pending_shortfall_resolution === "writeoff" ? "write off" : "return to source"}`);
      if (t.pending_overage_resolution) parts.push(`Over-receipt — ${t.pending_overage_resolution === "keep" ? "keep at receiving branch" : "return to source"}`);
      resolutionLine = `<div class="muted" style="margin-top:6px">Proposed resolution (awaiting source approval): ${parts.join("; ")}${t.pending_note ? ` — ${esc(t.pending_note)}` : ""}${t.proposed_by ? ` · by ${esc(t.proposed_by)}` : ""}</div>`;
    }
    const statusText = t.status === "pending_approval" ? "awaiting approval" : t.status;
    const dates = [
      `Created: ${fmtDateTime(t.created_at)}${t.created_by ? ` · ${esc(t.created_by)}` : ""}`,
      t.dispatched_at ? `Dispatched: ${fmtDateTime(t.dispatched_at)}${t.dispatched_by ? ` · ${esc(t.dispatched_by)}` : ""}` : "",
      t.received_at ? `Received: ${fmtDateTime(t.received_at)}${t.received_by ? ` · ${esc(t.received_by)}` : ""}` : "",
      t.resolved_at ? `Resolved: ${fmtDateTime(t.resolved_at)}${(t.approved_by || t.resolved_by) ? ` · ${esc(t.approved_by || t.resolved_by)}` : ""}` : "",
    ].filter(Boolean).join("<br/>");
    const body = `<div class="row"><div><img src="${window.location.origin}/mobimed-logo.png" alt="" style="height:46px;margin-bottom:6px"/><h1>MobiMed Pharmacy</h1><h2>${t.type === "request" ? "Stock Request" : "Stock Transfer"} ${esc(t.reference)}</h2><div class="muted">${esc(t.from_branch_name)} &rarr; ${esc(t.to_branch_name)}</div><div class="muted">Status: ${statusText}</div></div>
      <div class="muted" style="text-align:right">${dates}</div></div>
      <table><thead><tr><th>Item</th><th class="r">Dispatched</th><th class="r">Received</th><th class="r">Variance</th><th class="r">Settled</th><th class="r">Cost/unit</th><th class="r">Line total</th></tr></thead>
      <tbody>${rows}</tbody>
      <tfoot><tr><td colspan="6" class="r">${resolved ? "Total settled cost value" : "Total cost value"}</td><td class="r">${fmtGHS(grand)}</td></tr></tfoot></table>
      ${t.note ? `<div class="muted" style="margin-top:14px">Note: ${esc(t.note)}</div>` : ""}
      ${resolutionLine}
      <div class="muted" style="margin-top:8px;font-size:11px">${received ? "Settled = units retained at the destination after any agreed resolution. " : ""}Prices shown are cost prices. Generated by MobiMed inventory.</div>`;
    printDoc(`${t.reference}`, body);
  };

  const fromOptions = form.type === "request" ? warehouses : branches;
  const inTransit = transfers.filter((t) => t.status === "dispatched");
  const isSuper = (user?.permissions || []).includes("*");
  const myBranchIds = user?.branch_ids || [];
  const canApprove = (t) => isSuper || myBranchIds.includes(t.from_branch_id);
  const canReceive = (t) => isSuper || myBranchIds.includes(t.to_branch_id);
  const pendingApproval = transfers.filter((t) => t.status === "pending_approval" && canApprove(t));
  const proposedLabel = (t) => {
    const parts = [];
    if (t.pending_shortfall_resolution) parts.push(`shortfall → ${t.pending_shortfall_resolution === "writeoff" ? "write off" : "return to source"}`);
    if (t.pending_overage_resolution) parts.push(`over-receipt → ${t.pending_overage_resolution === "keep" ? "keep at receiver" : "return to source"}`);
    return parts.join(" · ");
  };

  return (
    <div>
      <PageHeader title="Stock Transfers" subtitle="Draft, dispatch (FEFO), receive with quantity confirmation, and reconcile discrepancies"
        actions={can("inventory.transfer") && <div className="flex gap-2">
          <Button variant="outline" data-testid="new-request-btn" onClick={() => openNew("request")}><Send className="h-4 w-4 mr-1.5" /> Request from warehouse</Button>
          <Button data-testid="new-transfer-btn" onClick={() => openNew("transfer")}><Plus className="h-4 w-4 mr-1.5" /> New transfer</Button>
        </div>} />

      {pendingApproval.length > 0 && (
        <Card className="mb-4 border-sky-300 dark:border-sky-500/30 bg-sky-50/60 dark:bg-sky-500/10" data-testid="pending-approval-panel">
          <div className="px-4 py-2.5 border-b border-sky-200 dark:border-sky-500/20 flex items-center gap-2">
            <Wrench className="h-4 w-4 text-sky-600" />
            <span className="text-sm font-semibold text-sky-800 dark:text-sky-300">Awaiting your approval — {pendingApproval.length} resolution{pendingApproval.length > 1 ? "s" : ""} proposed by receiving branches</span>
          </div>
          <div className="divide-y divide-sky-200/60 dark:divide-sky-500/20">
            {pendingApproval.map((t) => (
              <div key={t.id} className="px-4 py-2.5 flex items-center justify-between text-sm gap-3" data-testid={`pending-approval-${t.reference}`}>
                <div className="min-w-0">
                  <span className="font-mono text-xs text-sky-700 cursor-pointer" onClick={() => setSel(t)}>{t.reference}</span>
                  <span className="text-muted-foreground ml-2">{t.from_branch_name} → {t.to_branch_name}</span>
                  <div className="text-xs text-muted-foreground mt-0.5">Proposed by {t.proposed_by}: {proposedLabel(t)}{t.pending_note ? ` · "${t.pending_note}"` : ""}</div>
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  <Button size="sm" data-testid={`approve-res-panel-${t.reference}`} onClick={() => approveRes(t)}>Approve</Button>
                  <Button size="sm" variant="outline" className="text-rose-700 border-rose-300" data-testid={`reject-res-panel-${t.reference}`} onClick={() => openReject(t)}>Reject</Button>
                </div>
              </div>
            ))}
          </div>
        </Card>
      )}

      {inTransit.length > 0 && (
        <Card className="mb-4 border-amber-300 dark:border-amber-500/30 bg-amber-50/60 dark:bg-amber-500/10" data-testid="in-transit-panel">
          <div className="px-4 py-2.5 border-b border-amber-200 dark:border-amber-500/20 flex items-center gap-2">
            <Truck className="h-4 w-4 text-amber-600" />
            <span className="text-sm font-semibold text-amber-800 dark:text-amber-300">In transit — {inTransit.length} dispatched, awaiting receipt</span>
          </div>
          <div className="divide-y divide-amber-200/60 dark:divide-amber-500/20">
            {inTransit.map((t) => {
              const age = ageLabel(t.dispatched_at);
              const stale = t.dispatched_at && (Date.now() - new Date(t.dispatched_at).getTime()) > 86400000;
              return (
                <div key={t.id} className="px-4 py-2 flex items-center justify-between text-sm" data-testid={`in-transit-${t.reference}`}>
                  <div className="min-w-0">
                    <span className="font-mono text-xs text-sky-700 cursor-pointer" onClick={() => setSel(t)}>{t.reference}</span>
                    <span className="text-muted-foreground ml-2">{t.from_branch_name} → {t.to_branch_name}</span>
                  </div>
                  <div className="flex items-center gap-3 shrink-0">
                    <span className={`text-xs font-medium ${stale ? "text-rose-600" : "text-amber-700 dark:text-amber-300"}`}>{age} in transit{stale ? " · overdue" : ""}</span>
                    {can("inventory.transfer") && canReceive(t) && <Button size="sm" data-testid={`intransit-receive-${t.reference}`} onClick={() => openReceive(t)}><PackageCheck className="h-3.5 w-3.5 mr-1" /> Receive</Button>}
                  </div>
                </div>
              );
            })}
          </div>
        </Card>
      )}

      <Card className="shadow-sm overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-muted text-xs uppercase tracking-wider text-muted-foreground text-left">
            <tr><th className="py-2.5 px-3">Reference</th><th className="py-2.5 px-3">Type</th><th className="py-2.5 px-3">From</th><th className="py-2.5 px-3">To</th>
            <th className="py-2.5 px-3 text-right">Lines</th><th className="py-2.5 px-3">Status</th><th className="py-2.5 px-3">Created</th><th className="py-2.5 px-3 text-right">Actions</th></tr>
          </thead>
          <tbody>
            {transfers.length === 0 && <tr><td colSpan={8} className="py-10 text-center text-muted-foreground"><ArrowLeftRight className="h-8 w-8 mx-auto mb-2 opacity-40" />No transfers</td></tr>}
            {transfers.map((t) => (
              <tr key={t.id} className="border-t border-border hover:bg-muted/50">
                <td className="py-2 px-3 font-mono text-xs cursor-pointer text-sky-700" onClick={() => setSel(t)} data-testid={`transfer-ref-${t.reference}`}>{t.reference}</td>
                <td className="py-2 px-3 text-muted-foreground capitalize">{t.type === "request" ? "Request" : "Transfer"}</td>
                <td className="py-2 px-3 text-muted-foreground">{t.from_branch_name}</td>
                <td className="py-2 px-3 text-muted-foreground">{t.to_branch_name}</td>
                <td className="py-2 px-3 text-right font-mono">{t.lines.length}</td>
                <td className="py-2 px-3"><StatusBadge value={t.status} /></td>
                <td className="py-2 px-3 text-muted-foreground text-xs">{fmtDateTime(t.created_at)}</td>
                <td className="py-2 px-3 text-right">
                  <div className="flex items-center justify-end gap-1.5">
                    <Button size="sm" variant="ghost" data-testid={`print-${t.reference}`} onClick={() => printTransfer(t)}><Printer className="h-3.5 w-3.5" /></Button>
                    {can("inventory.transfer") && (t.status === "draft" || t.status === "requested") && <Button size="sm" variant="ghost" data-testid={`edit-${t.reference}`} onClick={() => openEdit(t)}><Pencil className="h-3.5 w-3.5" /></Button>}
                    {can("inventory.transfer") && (t.status === "draft" || t.status === "requested") && <Button size="sm" variant="outline" data-testid={`dispatch-${t.reference}`} onClick={() => act(t.id, "dispatch")}><Truck className="h-3.5 w-3.5 mr-1" /> Dispatch</Button>}
                    {can("inventory.transfer") && t.status === "dispatched" && canReceive(t) && <Button size="sm" data-testid={`receive-${t.reference}`} onClick={() => openReceive(t)}><PackageCheck className="h-3.5 w-3.5 mr-1" /> Receive</Button>}
                    {can("inventory.transfer") && t.status === "discrepancy" && <Button size="sm" variant="outline" className="text-amber-700 border-amber-300" data-testid={`resolve-${t.reference}`} onClick={() => openResolve(t)}><Wrench className="h-3.5 w-3.5 mr-1" /> Resolve</Button>}
                    {can("inventory.transfer") && t.status === "pending_approval" && canApprove(t) && <>
                      <Button size="sm" data-testid={`approve-res-${t.reference}`} onClick={() => approveRes(t)}>Approve</Button>
                      <Button size="sm" variant="outline" className="text-rose-700 border-rose-300" data-testid={`reject-res-${t.reference}`} onClick={() => openReject(t)}>Reject</Button>
                    </>}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </Card>

      {/* Create / Request dialog */}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-lg [&>*]:min-w-0">
          <DialogHeader>
            <DialogTitle>{form.type === "request" ? "Request stock from warehouse" : "New stock transfer"}</DialogTitle>
            <DialogDescription>{form.type === "request" ? "Requests can only be raised against a warehouse location. It is saved as a draft for the warehouse to dispatch." : "Saved as a draft. Dispatch it to allocate stock (FEFO) from the source branch."}</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <div><Label>{form.type === "request" ? "Warehouse (source)" : "From (source)"}</Label>
                <Select value={form.from_branch_id} onValueChange={(v) => setForm((f) => ({ ...f, from_branch_id: v }))}>
                  <SelectTrigger data-testid="transfer-from-select"><SelectValue placeholder="Source" /></SelectTrigger>
                  <SelectContent>{fromOptions.map((b) => <SelectItem key={b.id} value={b.id}>{b.name}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div><Label>{form.type === "request" ? "Deliver to (destination)" : "To (destination)"}</Label>
                <Select value={form.to_branch_id} onValueChange={(v) => setForm((f) => ({ ...f, to_branch_id: v }))}>
                  <SelectTrigger data-testid="transfer-to-select"><SelectValue placeholder="Destination" /></SelectTrigger>
                  <SelectContent>{(form.type === "request" ? branches : allActive).filter((b) => b.id !== form.from_branch_id).map((b) => <SelectItem key={b.id} value={b.id}>{b.name}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            </div>
            <div>
              <div className="flex flex-wrap items-center justify-between gap-1 mb-1"><Label>Items</Label>
                <div className="flex gap-1">
                  {form.type === "request" && <Button size="sm" variant="outline" disabled={suggesting} onClick={suggest} data-testid="transfer-suggest-btn"><Sparkles className="h-3.5 w-3.5 mr-1" /> {suggesting ? "Checking…" : "Suggest from low stock"}</Button>}
                  <Button size="sm" variant="ghost" onClick={addLine}><Plus className="h-3.5 w-3.5 mr-1" /> Add</Button>
                </div>
              </div>
              <div className="space-y-2">
                {form.lines.map((l, i) => {
                  const avail = srcStock[l.product_id];
                  const over = form.type === "transfer" && avail != null && Number(l.quantity) > avail;
                  return (
                  <div key={i}>
                    <div className="flex gap-2">
                      <ProductCombobox products={products} value={l.product_id} onChange={(v) => setLine(i, "product_id", v)} testId={`transfer-product-${i}`} className="flex-1" />
                      <Input type="number" min={1} className={`w-20 ${over ? "border-rose-400" : ""}`} value={l.quantity} onChange={(e) => setLine(i, "quantity", e.target.value)} data-testid={`transfer-qty-${i}`} />
                      <Button size="icon" variant="ghost" onClick={() => rmLine(i)}><Trash2 className="h-4 w-4 text-rose-500" /></Button>
                    </div>
                    {l.product_id && form.type === "transfer" && form.from_branch_id && (
                      <div className={`text-[11px] mt-0.5 ml-0.5 ${over ? "text-rose-600" : "text-muted-foreground"}`} data-testid={`transfer-avail-${i}`}>
                        Available at source: {avail ?? 0}{over ? " — exceeds stock, dispatch will fail" : ""}
                      </div>
                    )}
                  </div>
                  );
                })}
              </div>
            </div>
            <div><Label>Note</Label><Input value={form.note} onChange={(e) => setForm((f) => ({ ...f, note: e.target.value }))} /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
            <Button data-testid="submit-transfer-btn" onClick={create}>{form.type === "request" ? "Submit request" : "Create draft"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit draft dialog (warehouse adjusts what it can supply) */}
      <Dialog open={editOpen} onOpenChange={setEditOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Edit {editT?.reference}</DialogTitle>
            <DialogDescription>Adjust quantities to what {editT?.from_branch_name} can actually supply before dispatching. Availability is visible to the supplying location only.</DialogDescription>
          </DialogHeader>
          {editT && (
            <div className="border-y border-border divide-y divide-border">
              {editLines.length === 0 && <div className="py-6 text-center text-sm text-muted-foreground">No items left</div>}
              {editLines.map((l) => {
                const avail = editStock[l.product_id];
                const over = avail != null && Number(l.quantity) > avail;
                return (
                  <div key={l.product_id} className="py-2">
                    <div className="flex items-center gap-2">
                      <div className="flex-1 min-w-0"><div className="font-medium truncate">{l.generic_name}</div><div className="text-xs text-muted-foreground">{l.sku}</div></div>
                      <Input type="number" min={0} className={`w-24 text-right ${over ? "border-rose-400" : ""}`} data-testid={`edit-qty-${l.sku}`} value={l.quantity} onChange={(e) => setEditQty(l.product_id, e.target.value)} />
                      <Button size="icon" variant="ghost" onClick={() => rmEditLine(l.product_id)}><Trash2 className="h-4 w-4 text-rose-500" /></Button>
                    </div>
                    <div className={`text-[11px] mt-0.5 ${over ? "text-rose-600" : "text-muted-foreground"}`} data-testid={`edit-avail-${l.sku}`}>Available: {avail ?? 0}{over ? " — exceeds stock" : ""}</div>
                  </div>
                );
              })}
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditOpen(false)}>Cancel</Button>
            <Button data-testid="submit-edit-btn" onClick={submitEdit}>Save changes</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Receive dialog */}
      <Dialog open={recvOpen} onOpenChange={setRecvOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Receive {recvT?.reference}</DialogTitle>
            <DialogDescription>Confirm the quantity actually received per item. A shortfall or an over-receipt is flagged as a discrepancy for the sending branch to reconcile.</DialogDescription>
          </DialogHeader>
          {recvT && (
            <div className="border-y border-border overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="text-xs uppercase text-muted-foreground text-left"><tr><th className="py-2 px-2">Item</th><th className="py-2 px-2 text-right">Dispatched</th><th className="py-2 px-2 text-right">Received</th></tr></thead>
                <tbody>
                  {recvT.lines.map((l) => { const disp = lineDispatched(l); return (
                    <tr key={l.product_id} className="border-t border-border">
                      <td className="py-1.5 px-2">{l.generic_name}<span className="text-xs text-muted-foreground ml-1">{l.sku}</span></td>
                      <td className="py-1.5 px-2 text-right font-mono">{disp}</td>
                      <td className="py-1.5 px-2 text-right"><Input type="number" min={0} className="h-8 w-24 ml-auto text-right" data-testid={`receive-qty-${l.sku}`} value={recvQty[l.product_id] ?? disp} onChange={(e) => setRecvQty((m) => ({ ...m, [l.product_id]: e.target.value }))} /></td>
                    </tr>
                  ); })}
                </tbody>
              </table>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setRecvOpen(false)}>Cancel</Button>
            <Button data-testid="submit-receive-btn" onClick={submitReceive}>Confirm receipt</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Resolve discrepancy dialog */}
      <Dialog open={resOpen} onOpenChange={setResOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><AlertTriangle className="h-4 w-4 text-amber-600" /> Resolve discrepancy · {resT?.reference}</DialogTitle>
            <DialogDescription>Investigate the discrepancy, then propose how to reconcile it. Your proposal is sent to the sending branch, which must approve it before any stock moves.</DialogDescription>
          </DialogHeader>
          {resT && (
            <div className="space-y-3 text-sm">
              <div className="border-y border-border divide-y divide-border">
                {resT.lines.filter((l) => (l.shortfall || 0) > 0 || (l.overage || 0) > 0).map((l) => (
                  <div key={l.product_id} className="py-2 flex justify-between" data-testid={`variance-${l.sku}`}>
                    <span>{l.generic_name}</span>
                    <span className="font-mono text-amber-700">{(l.shortfall || 0) > 0 ? `short ${l.shortfall}` : `over ${l.overage}`} of {lineDispatched(l)}</span>
                  </div>
                ))}
              </div>
              {resT.lines.some((l) => (l.shortfall || 0) > 0) && (
                <div><Label>Shortfall (received less than sent)</Label>
                  <Select value={sfRes} onValueChange={setSfRes}>
                    <SelectTrigger data-testid="shortfall-resolution-select"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="return_to_source">Return shortfall to source (found / investigated)</SelectItem>
                      <SelectItem value="writeoff">Write off (lost / damaged in transit)</SelectItem>
                    </SelectContent>
                  </Select>
                  <p className="text-xs text-muted-foreground mt-1">{sfRes === "return_to_source" ? "Adds the missing units back to the sending branch's stock." : "Records the missing units as lost; no stock is returned."}</p>
                </div>
              )}
              {resT.lines.some((l) => (l.overage || 0) > 0) && (
                <div><Label>Over-receipt (received more than sent)</Label>
                  <Select value={ovRes} onValueChange={setOvRes}>
                    <SelectTrigger data-testid="overage-resolution-select"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="return_to_source">Return extra units to source</SelectItem>
                      <SelectItem value="keep">Keep extra units at receiving branch</SelectItem>
                    </SelectContent>
                  </Select>
                  <p className="text-xs text-muted-foreground mt-1">{ovRes === "return_to_source" ? "Removes the extra units from this branch and adds them back to the sending branch's stock." : "Extra units stay in this branch's stock (valued at source cost)."}</p>
                </div>
              )}
              <div><Label>Investigation note</Label><Input data-testid="resolution-note" value={resNote} onChange={(e) => setResNote(e.target.value)} placeholder="What happened?" /></div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setResOpen(false)}>Cancel</Button>
            <Button data-testid="submit-resolve-btn" onClick={submitResolve}>Propose resolution</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Reject proposal dialog */}
      <Dialog open={rejOpen} onOpenChange={setRejOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Reject resolution · {rejT?.reference}</DialogTitle>
            <DialogDescription>Rejecting sends the discrepancy back to {rejT?.to_branch_name} to propose a different resolution. No stock is changed.</DialogDescription>
          </DialogHeader>
          <div className="text-sm space-y-3">
            <div className="text-muted-foreground">Proposed: {rejT ? proposedLabel(rejT) : ""}{rejT?.pending_note ? ` · "${rejT.pending_note}"` : ""}</div>
            <div><Label>Reason for rejecting</Label><Input data-testid="reject-note" value={rejNote} onChange={(e) => setRejNote(e.target.value)} placeholder="Why is this not acceptable?" /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setRejOpen(false)}>Cancel</Button>
            <Button className="bg-rose-600 hover:bg-rose-700" data-testid="submit-reject-btn" onClick={submitReject}>Reject proposal</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Detail dialog */}
      <Dialog open={!!sel} onOpenChange={(o) => !o && setSel(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center justify-between gap-4"><span>{sel?.reference}</span>{sel && <Button size="sm" variant="outline" data-testid="print-detail-btn" onClick={() => printTransfer(sel)}><Printer className="h-3.5 w-3.5 mr-1.5" /> Print</Button>}</DialogTitle>
            <DialogDescription>{sel?.from_branch_name} → {sel?.to_branch_name}</DialogDescription>
          </DialogHeader>
          {sel && (
            <div className="text-sm">
              <div className="flex justify-between text-muted-foreground mb-3"><span className="capitalize">{sel.type} · {sel.from_branch_name} → {sel.to_branch_name}</span><StatusBadge value={sel.status} /></div>
              <div className="divide-y divide-border border-y border-border">
                {sel.lines.map((l, i) => (
                  <div key={i} className="py-2">
                    <div className="flex justify-between">
                      <span className="font-medium">{l.generic_name}</span>
                      <span className="font-mono">× {lineDispatched(l) || l.quantity}{l.received_qty != null ? ` · recv ${l.received_qty}` : ""}{(l.shortfall || 0) > 0 ? ` · short ${l.shortfall}` : ""}{(l.overage || 0) > 0 ? ` · over ${l.overage}` : ""}</span>
                    </div>
                    {l.allocations?.length > 0 && <div className="text-xs text-muted-foreground font-mono mt-0.5">{l.allocations.map((a) => `${a.batch_no}(${a.quantity}) @ ${fmtGHS(a.cost_price || 0)}`).join(", ")}</div>}
                  </div>
                ))}
              </div>
              <div className="flex justify-between font-semibold mt-3"><span>Total cost value</span><span className="font-mono">{fmtGHS(sel.lines.reduce((s, l) => s + lineCostTotal(l), 0))}</span></div>
              {sel.status === "pending_approval" && <p className="text-amber-700 mt-3 text-xs" data-testid="detail-pending">Awaiting source approval — proposed by {sel.proposed_by}: {proposedLabel(sel)}{sel.pending_note ? ` · "${sel.pending_note}"` : ""}</p>}
              {sel.status === "discrepancy" && sel.rejection_note && <p className="text-rose-700 mt-3 text-xs">Previous proposal rejected by {sel.rejected_by}: {sel.rejection_note}</p>}
              {sel.status === "resolved" && <p className="text-muted-foreground mt-3 text-xs">Resolved{sel.approved_by ? ` (approved by ${sel.approved_by})` : ""}: {[sel.shortfall_resolution ? `shortfall ${sel.shortfall_resolution === "writeoff" ? "written off" : "returned to source"}` : "", sel.overage_resolution ? `over-receipt ${sel.overage_resolution === "keep" ? "kept at receiver" : "returned to source"}` : ""].filter(Boolean).join(" · ") || (sel.resolution === "writeoff" ? "Write-off" : "Returned to source")}{sel.resolution_note ? ` — ${sel.resolution_note}` : ""}</p>}
              {sel.note && <p className="text-muted-foreground mt-2">{sel.note}</p>}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
